import * as i0 from '@angular/core';
import { Injectable, NgModule, InjectionToken } from '@angular/core';
import * as i1$1 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i1 from '@spartacus/core';
import { CxEvent, facadeFactory, provideDefaultConfigFactory, provideDefaultConfig, Config } from '@spartacus/core';
import { CmsPageGuard, PageLayoutComponent, PageEvent, LAUNCH_CALLER } from '@spartacus/storefront';
import { Subscription } from 'rxjs';

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const defaultCartConfig = {
  cart: {
    validation: {
      enabled: false
    },
    selectiveCart: {
      enabled: false
    }
  }
};

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const defaultCartRoutingConfig = {
  routing: {
    routes: {
      cart: {
        paths: ['cart']
      }
    }
  }
};

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const ORDER_ENTRIES_CONTEXT = Symbol('ORDER_ENTRIES_CONTEXT');

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Base cart event. Most cart events should have these properties.
 */
class CartEvent extends CxEvent {}
let CreateCartEvent = /*#__PURE__*/(() => {
  class CreateCartEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CreateCartEvent';
    }
  }
  return CreateCartEvent;
})();
let CreateCartSuccessEvent = /*#__PURE__*/(() => {
  class CreateCartSuccessEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CreateCartSuccessEvent';
    }
  }
  return CreateCartSuccessEvent;
})();
let CreateCartFailEvent = /*#__PURE__*/(() => {
  class CreateCartFailEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CreateCartFailEvent';
    }
  }
  return CreateCartFailEvent;
})();
let CartAddEntryEvent = /*#__PURE__*/(() => {
  class CartAddEntryEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CartAddEntryEvent';
    }
  }
  return CartAddEntryEvent;
})();
let CartAddEntrySuccessEvent = /*#__PURE__*/(() => {
  class CartAddEntrySuccessEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CartAddEntrySuccessEvent';
    }
  }
  return CartAddEntrySuccessEvent;
})();
let CartAddEntryFailEvent = /*#__PURE__*/(() => {
  class CartAddEntryFailEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CartAddEntryFailEvent';
    }
  }
  return CartAddEntryFailEvent;
})();
let CartRemoveEntryFailEvent = /*#__PURE__*/(() => {
  class CartRemoveEntryFailEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CartRemoveEntryFailEvent';
    }
  }
  return CartRemoveEntryFailEvent;
})();
let CartRemoveEntrySuccessEvent = /*#__PURE__*/(() => {
  class CartRemoveEntrySuccessEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CartRemoveEntrySuccessEvent';
    }
  }
  return CartRemoveEntrySuccessEvent;
})();
let CartUpdateEntrySuccessEvent = /*#__PURE__*/(() => {
  class CartUpdateEntrySuccessEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CartUpdateEntrySuccessEvent';
    }
  }
  return CartUpdateEntrySuccessEvent;
})();
let CartUpdateEntryFailEvent = /*#__PURE__*/(() => {
  class CartUpdateEntryFailEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CartUpdateEntryFailEvent';
    }
  }
  return CartUpdateEntryFailEvent;
})();
let CartUiEventAddToCart = /*#__PURE__*/(() => {
  class CartUiEventAddToCart extends CxEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'CartUiEventAddToCart';
    }
  }
  return CartUiEventAddToCart;
})();
/**
 * Fired when the cart has been successfully merged.
 */
let MergeCartSuccessEvent = /*#__PURE__*/(() => {
  class MergeCartSuccessEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'MergeCartSuccessEvent';
    }
  }
  return MergeCartSuccessEvent;
})();
/**
 * Triggers the loading of the cart.
 */
let LoadCartEvent = /*#__PURE__*/(() => {
  class LoadCartEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'LoadCartEvent';
    }
  }
  return LoadCartEvent;
})();
/** Removes the cart. */
let RemoveCartEvent = /*#__PURE__*/(() => {
  class RemoveCartEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'RemoveCartEvent';
    }
  }
  return RemoveCartEvent;
})();
/**
 * Fired when the cart has been deleted.
 */
let DeleteCartEvent = /*#__PURE__*/(() => {
  class DeleteCartEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'DeleteCartEvent';
    }
  }
  return DeleteCartEvent;
})();
let DeleteCartSuccessEvent = /*#__PURE__*/(() => {
  class DeleteCartSuccessEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'DeleteCartSuccessEvent';
    }
  }
  return DeleteCartSuccessEvent;
})();
let DeleteCartFailEvent = /*#__PURE__*/(() => {
  class DeleteCartFailEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'DeleteCartFailEvent';
    }
  }
  return DeleteCartFailEvent;
})();
let AddCartVoucherEvent = /*#__PURE__*/(() => {
  class AddCartVoucherEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'AddCartVoucherEvent';
    }
  }
  return AddCartVoucherEvent;
})();
let AddCartVoucherSuccessEvent = /*#__PURE__*/(() => {
  class AddCartVoucherSuccessEvent extends AddCartVoucherEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'AddCartVoucherSuccessEvent';
    }
  }
  return AddCartVoucherSuccessEvent;
})();
let AddCartVoucherFailEvent = /*#__PURE__*/(() => {
  class AddCartVoucherFailEvent extends AddCartVoucherEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'AddCartVoucherFailEvent';
    }
  }
  return AddCartVoucherFailEvent;
})();
let RemoveCartVoucherEvent = /*#__PURE__*/(() => {
  class RemoveCartVoucherEvent extends CartEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'RemoveCartVoucherEvent';
    }
  }
  return RemoveCartVoucherEvent;
})();
let RemoveCartVoucherSuccessEvent = /*#__PURE__*/(() => {
  class RemoveCartVoucherSuccessEvent extends RemoveCartVoucherEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'RemoveCartVoucherSuccessEvent';
    }
  }
  return RemoveCartVoucherSuccessEvent;
})();
let RemoveCartVoucherFailEvent = /*#__PURE__*/(() => {
  class RemoveCartVoucherFailEvent extends RemoveCartVoucherEvent {
    /**
     * Event's type
     */
    static {
      this.type = 'RemoveCartVoucherFailEvent';
    }
  }
  return RemoveCartVoucherFailEvent;
})();
/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const CART_BASE_FEATURE = 'cartBase';
const CART_BASE_CORE_FEATURE = 'cartBaseCore';
const MINI_CART_FEATURE = 'miniCart';
const ADD_TO_CART_FEATURE = 'addToCart';

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let MultiCartFacade = /*#__PURE__*/(() => {
  class MultiCartFacade {
    static {
      this.ɵfac = function MultiCartFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MultiCartFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MultiCartFacade,
        factory: () => (() => facadeFactory({
          facade: MultiCartFacade,
          feature: CART_BASE_CORE_FEATURE,
          methods: ['getCart', 'getCarts', 'getCartEntity', 'isStable', 'createCart', 'mergeToCurrentCart', 'loadCart', 'getEntries', 'getLastEntry', 'addEntry', 'addEntries', 'removeEntry', 'updateEntry', 'getEntry', 'assignEmail', 'removeCart', 'deleteCart', 'reloadCart', 'getCartIdByType'],
          async: true
        }))(),
        providedIn: 'root'
      });
    }
  }
  return MultiCartFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let MultiCartEventListener = /*#__PURE__*/(() => {
  class MultiCartEventListener {
    constructor(eventService, multiCartFacade) {
      this.eventService = eventService;
      this.multiCartFacade = multiCartFacade;
      this.subscriptions = new Subscription();
      this.onCartBaseAction();
    }
    /**
     * Registers events for the cart base actions.
     */
    onCartBaseAction() {
      this.subscriptions.add(this.eventService.get(LoadCartEvent).subscribe(({
        userId,
        cartId
      }) => {
        if (userId && cartId) {
          this.multiCartFacade.loadCart({
            userId,
            cartId
          });
        }
      }));
      this.subscriptions.add(this.eventService.get(RemoveCartEvent).subscribe(({
        cartId
      }) => {
        this.multiCartFacade.removeCart(cartId);
      }));
    }
    ngOnDestroy() {
      this.subscriptions.unsubscribe();
    }
    static {
      this.ɵfac = function MultiCartEventListener_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MultiCartEventListener)(i0.ɵɵinject(i1.EventService), i0.ɵɵinject(MultiCartFacade));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MultiCartEventListener,
        factory: MultiCartEventListener.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MultiCartEventListener;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CartBaseEventModule = /*#__PURE__*/(() => {
  class CartBaseEventModule {
    constructor(_multiCartEventListener) {
      // Intentional empty constructor
    }
    static {
      this.ɵfac = function CartBaseEventModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CartBaseEventModule)(i0.ɵɵinject(MultiCartEventListener));
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: CartBaseEventModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return CartBaseEventModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const ActiveCartOrderEntriesContextToken = new InjectionToken('ActiveCartOrderEntriesContext');

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
function defaultCartComponentsConfig() {
  const config = {
    featureModules: {
      [CART_BASE_FEATURE]: {
        cmsComponents: ['CartApplyCouponComponent', 'CartComponent', 'CartProceedToCheckoutComponent', 'CartTotalsComponent', 'SaveForLaterComponent', 'ClearCartComponent']
      },
      [MINI_CART_FEATURE]: {
        cmsComponents: ['MiniCartComponent']
      },
      [ADD_TO_CART_FEATURE]: {
        cmsComponents: ['ProductAddToCartComponent']
      },
      // By default core is bundled together with components.
      // The cart lib should keep using this default.
      //
      // While the lazy loading configurations make it possible to
      // split the core part and the components part, it is required that
      // they stay together for the cart lib.  This compromise is required to
      // optimize performances by delaying the moment the cart lib is loaded and
      // making sure cart lib is loaded when needed.
      [CART_BASE_CORE_FEATURE]: CART_BASE_FEATURE
    }
  };
  return config;
}
let CartBaseRootModule = /*#__PURE__*/(() => {
  class CartBaseRootModule {
    static {
      this.ɵfac = function CartBaseRootModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CartBaseRootModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: CartBaseRootModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [provideDefaultConfigFactory(defaultCartComponentsConfig), provideDefaultConfig(defaultCartConfig), provideDefaultConfig(defaultCartRoutingConfig)],
        imports: [CartBaseEventModule, RouterModule.forChild([{
          // @ts-ignore
          path: null,
          canActivate: [CmsPageGuard],
          component: PageLayoutComponent,
          data: {
            cxRoute: 'cart',
            cxContext: {
              [ORDER_ENTRIES_CONTEXT]: ActiveCartOrderEntriesContextToken
            }
          }
        }])]
      });
    }
  }
  return CartBaseRootModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CartConfig = /*#__PURE__*/(() => {
  class CartConfig {
    static {
      this.ɵfac = function CartConfig_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CartConfig)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CartConfig,
        factory: function CartConfig_Factory(__ngFactoryType__) {
          let __ngConditionalFactory__ = null;
          if (__ngFactoryType__) {
            __ngConditionalFactory__ = new (__ngFactoryType__ || CartConfig)();
          } else {
            __ngConditionalFactory__ = i0.ɵɵinject(Config);
          }
          return __ngConditionalFactory__;
        },
        providedIn: 'root'
      });
    }
  }
  return CartConfig;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Indicates that a user visited a cart page.
 */
let CartPageEvent = /*#__PURE__*/(() => {
  class CartPageEvent extends PageEvent {
    /** event's type */
    static {
      this.type = 'CartPageEvent';
    }
  }
  return CartPageEvent;
})();
/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let ActiveCartFacade = /*#__PURE__*/(() => {
  class ActiveCartFacade {
    static {
      this.ɵfac = function ActiveCartFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ActiveCartFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ActiveCartFacade,
        factory: () => (() => facadeFactory({
          facade: ActiveCartFacade,
          feature: CART_BASE_CORE_FEATURE,
          methods: ['getActive', 'takeActive', 'getActiveCartId', 'takeActiveCartId', 'getEntries', 'getLastEntry', 'getLoading', 'isStable', 'addEntry', 'removeEntry', 'updateEntry', 'getEntry', 'addEmail', 'getAssignedUser', 'isGuestCart', 'addEntries', 'requireLoadedCart', 'reloadActiveCart', 'hasPickupItems', 'hasDeliveryItems', 'getPickupEntries', 'getDeliveryEntries'],
          async: true
        }))(),
        providedIn: 'root'
      });
    }
  }
  return ActiveCartFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CartAccessCodeFacade = /*#__PURE__*/(() => {
  class CartAccessCodeFacade {
    static {
      this.ɵfac = function CartAccessCodeFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CartAccessCodeFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CartAccessCodeFacade,
        factory: () => (() => facadeFactory({
          facade: CartAccessCodeFacade,
          feature: CART_BASE_CORE_FEATURE,
          methods: ['getCartAccessCode']
        }))(),
        providedIn: 'root'
      });
    }
  }
  return CartAccessCodeFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CartGuestUserFacade = /*#__PURE__*/(() => {
  class CartGuestUserFacade {
    static {
      this.ɵfac = function CartGuestUserFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CartGuestUserFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CartGuestUserFacade,
        factory: () => (() => facadeFactory({
          facade: CartGuestUserFacade,
          feature: CART_BASE_CORE_FEATURE,
          methods: ['createCartGuestUser', 'updateCartGuestUser']
        }))(),
        providedIn: 'root'
      });
    }
  }
  return CartGuestUserFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CartValidationFacade = /*#__PURE__*/(() => {
  class CartValidationFacade {
    static {
      this.ɵfac = function CartValidationFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CartValidationFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CartValidationFacade,
        factory: () => (() => facadeFactory({
          facade: CartValidationFacade,
          feature: CART_BASE_CORE_FEATURE,
          methods: ['validateCart', 'getValidationResults']
        }))(),
        providedIn: 'root'
      });
    }
  }
  return CartValidationFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let CartVoucherFacade = /*#__PURE__*/(() => {
  class CartVoucherFacade {
    static {
      this.ɵfac = function CartVoucherFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CartVoucherFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CartVoucherFacade,
        factory: () => (() => facadeFactory({
          facade: CartVoucherFacade,
          feature: CART_BASE_CORE_FEATURE,
          methods: ['addVoucher', 'removeVoucher', 'getAddVoucherResultError', 'getAddVoucherResultSuccess', 'getAddVoucherResultLoading', 'resetAddVoucherProcessingState'],
          async: true
        }))(),
        providedIn: 'root'
      });
    }
  }
  return CartVoucherFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let SelectiveCartFacade = /*#__PURE__*/(() => {
  class SelectiveCartFacade {
    static {
      this.ɵfac = function SelectiveCartFacade_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SelectiveCartFacade)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SelectiveCartFacade,
        factory: () => (() => facadeFactory({
          facade: SelectiveCartFacade,
          feature: CART_BASE_CORE_FEATURE,
          methods: ['getCart', 'getEntries', 'isStable', 'addEntry', 'removeEntry', 'updateEntry', 'getEntry'],
          async: true
        }))(),
        providedIn: 'root'
      });
    }
  }
  return SelectiveCartFacade;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
LAUNCH_CALLER['CLEAR_CART'] = 'CLEAR_CART';
LAUNCH_CALLER['ADDED_TO_CART'] = 'ADDED_TO_CART';

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Context for `CartItemComponent`.
 */
let CartItemContext = /*#__PURE__*/(() => {
  class CartItemContext {
    static {
      this.ɵfac = function CartItemContext_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CartItemContext)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CartItemContext,
        factory: CartItemContext.ɵfac
      });
    }
  }
  return CartItemContext;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/**
 * Identifiers of outlets inside the cart details view.
 */
var CartOutlets = /*#__PURE__*/function (CartOutlets) {
  CartOutlets["ITEM"] = "cx-cart-item";
  CartOutlets["LIST_ITEM"] = "cx-cart-item-list-row";
  CartOutlets["ITEM_DETAILS"] = "cx-cart-item.details";
  CartOutlets["ITEM_CONFIGURATOR_ISSUES"] = "cx-configurator-issues-notification";
  CartOutlets["ITEM_BUNDLE_DETAILS"] = "cx-cart-item.bundle-details";
  CartOutlets["ITEM_DELIVERY_DETAILS"] = "cx-cart-item.delivery-details";
  CartOutlets["ORDER_SUMMARY"] = "cx-order-summary";
  CartOutlets["CART_ITEM_LIST"] = "cx-cart-item-list";
  CartOutlets["ADD_TO_CART_CONTAINER"] = "cx-add-to-cart-container";
  CartOutlets["PICKUP_INFO"] = "cx-pickup-info";
  CartOutlets["ADD_TO_CART_PICKUP_OPTION"] = "cx-add-to-cart-pickup-option";
  CartOutlets["DELIVERY_MODE"] = "cx-delivery-mode";
  CartOutlets["ORDER_OVERVIEW"] = "cx-order-overview";
  CartOutlets["CPQ_QUOTE_MODULE"] = "cx-cpq-quote";
  CartOutlets["CPQ_QUOTE_HEADING"] = "cx-cpq-quote-heading";
  CartOutlets["CPQ_QUOTE"] = "cx-cpq-quote-offer";
  return CartOutlets;
}(CartOutlets || {});
/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
var PromotionLocation = /*#__PURE__*/function (PromotionLocation) {
  PromotionLocation["ActiveCart"] = "CART";
  PromotionLocation["Checkout"] = "CHECKOUT";
  PromotionLocation["Order"] = "ORDER";
  PromotionLocation["SaveForLater"] = "SAVE_FOR_LATER";
  PromotionLocation["SavedCart"] = "SAVED_CART";
  return PromotionLocation;
}(PromotionLocation || {});
/**
 * The possible types of 'abstract orders'.
 */
var AbstractOrderType = /*#__PURE__*/function (AbstractOrderType) {
  /**
   * Active cart
   */
  AbstractOrderType["CART"] = "Cart";
  AbstractOrderType["ORDER"] = "Order";
  AbstractOrderType["QUOTE"] = "Quote";
  AbstractOrderType["SAVED_CART"] = "SavedCart";
  return AbstractOrderType;
}(AbstractOrderType || {});
var CartType = /*#__PURE__*/function (CartType) {
  CartType["ACTIVE"] = "Active";
  CartType["WISH_LIST"] = "WishList";
  CartType["SELECTIVE"] = "Selective";
  CartType["NEW_CREATED"] = "NewCreated";
  return CartType;
}(CartType || {});
var CartValidationStatusCode = /*#__PURE__*/function (CartValidationStatusCode) {
  CartValidationStatusCode["NO_STOCK"] = "noStock";
  CartValidationStatusCode["LOW_STOCK"] = "lowStock";
  CartValidationStatusCode["REVIEW_CONFIGURATION"] = "reviewConfiguration";
  CartValidationStatusCode["PRICING_ERROR"] = "pricingError";
  CartValidationStatusCode["UNRESOLVABLE_ISSUES"] = "unresolvableIssues";
  return CartValidationStatusCode;
}(CartValidationStatusCode || {});
/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
var OrderEntriesSource = /*#__PURE__*/function (OrderEntriesSource) {
  OrderEntriesSource["ACTIVE_CART"] = "ACTIVE_CART";
  OrderEntriesSource["NEW_SAVED_CART"] = "NEW_SAVED_CART";
  OrderEntriesSource["QUICK_ORDER"] = "QUICK_ORDER";
  OrderEntriesSource["SAVED_CART"] = "SAVED_CART";
  OrderEntriesSource["ORDER_CONFIRMATION"] = "ORDER_CONFIRMATION";
  OrderEntriesSource["ORDER_DETAILS"] = "ORDER_DETAILS";
  OrderEntriesSource["UNIT_ORDER_DETAILS"] = "UNIT_ORDER_DETAILS";
  return OrderEntriesSource;
}(OrderEntriesSource || {});
/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
var ProductImportStatus = /*#__PURE__*/function (ProductImportStatus) {
  ProductImportStatus["SUCCESS"] = "success";
  ProductImportStatus["LOW_STOCK"] = "lowStock";
  ProductImportStatus["NO_STOCK"] = "noStock";
  ProductImportStatus["UNKNOWN_IDENTIFIER"] = "unknownIdentifier";
  ProductImportStatus["UNKNOWN_ERROR"] = "unknownError";
  ProductImportStatus["LIMIT_EXCEEDED"] = "limitExceeded";
  return ProductImportStatus;
}(ProductImportStatus || {});
/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const CART_NORMALIZER = new InjectionToken('CartNormalizer');
const ORDER_ENTRY_PROMOTIONS_NORMALIZER = new InjectionToken('OrderEntryPromotionsNormalizer');
const CART_MODIFICATION_NORMALIZER = new InjectionToken('CartModificationNormalizer');
const SAVE_CART_NORMALIZER = new InjectionToken('SaveCartNormalizer');
const CART_VOUCHER_NORMALIZER = new InjectionToken('CartVoucherNormalizer');

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2025 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
/** AUGMENTABLE_TYPES_END */

/**
 * Generated bundle index. Do not edit.
 */

export { ADD_TO_CART_FEATURE, AbstractOrderType, ActiveCartFacade, ActiveCartOrderEntriesContextToken, AddCartVoucherEvent, AddCartVoucherFailEvent, AddCartVoucherSuccessEvent, CART_BASE_CORE_FEATURE, CART_BASE_FEATURE, CART_MODIFICATION_NORMALIZER, CART_NORMALIZER, CART_VOUCHER_NORMALIZER, CartAccessCodeFacade, CartAddEntryEvent, CartAddEntryFailEvent, CartAddEntrySuccessEvent, CartBaseEventModule, CartBaseRootModule, CartConfig, CartEvent, CartGuestUserFacade, CartItemContext, CartOutlets, CartPageEvent, CartRemoveEntryFailEvent, CartRemoveEntrySuccessEvent, CartType, CartUiEventAddToCart, CartUpdateEntryFailEvent, CartUpdateEntrySuccessEvent, CartValidationFacade, CartValidationStatusCode, CartVoucherFacade, CreateCartEvent, CreateCartFailEvent, CreateCartSuccessEvent, DeleteCartEvent, DeleteCartFailEvent, DeleteCartSuccessEvent, LoadCartEvent, MINI_CART_FEATURE, MergeCartSuccessEvent, MultiCartEventListener, MultiCartFacade, ORDER_ENTRIES_CONTEXT, ORDER_ENTRY_PROMOTIONS_NORMALIZER, OrderEntriesSource, ProductImportStatus, PromotionLocation, RemoveCartEvent, RemoveCartVoucherEvent, RemoveCartVoucherFailEvent, RemoveCartVoucherSuccessEvent, SAVE_CART_NORMALIZER, SelectiveCartFacade, defaultCartComponentsConfig };
